import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiErrorDetail } from '../lib/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[var(--brand-text)] items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1647513600981-95ad864195d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzc1Nzk1OTU0fDA&ixlib=rb-4.1.0&q=85"
          alt="Abstract architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="relative z-10 p-12 max-w-lg">
          <h1 className="text-5xl font-black tracking-tighter text-white mb-4">
            GetScribeDash
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            Upload handwritten documents. Get them digitized by verified transcribers. Fast, accurate, affordable.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[var(--brand-bg)]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <h1 className="text-4xl font-black tracking-tighter text-[var(--brand-text)]">GetScribeDash</h1>
          </div>
          
          <h2 className="text-2xl font-bold tracking-tight text-[var(--brand-text)] mb-1">Welcome back</h2>
          <p className="text-sm text-[var(--brand-text-sec)] mb-8">Sign in to your account</p>

          {error && (
            <div data-testid="login-error" className="mb-4 p-3 bg-[var(--brand-danger)]/10 border border-[var(--brand-danger)]/20 text-[var(--brand-danger)] text-sm rounded-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-sm font-semibold tracking-tight text-[var(--brand-text)]">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 rounded-sm border-[var(--brand-border)] focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold tracking-tight text-[var(--brand-text)]">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="rounded-sm border-[var(--brand-border)] focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)] pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--brand-text-sec)] hover:text-[var(--brand-text)]"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--brand-primary)] text-white rounded-sm px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-[var(--brand-text-sec)] text-center">
            Don't have an account?{' '}
            <Link to="/register" className="text-[var(--brand-primary)] font-medium hover:underline" data-testid="go-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
